import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomDatePicker from "common/CustomDatePicker";
import CustomSelect from "components/Common/custom-select";
import TableContainer from "components/Common/TableContainer";
import { useFormik } from "formik";
import * as Yup from "yup";
import Loader from "common/pageLoader";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { fetchEmployeeDropdownAutoList, fetchClaimReports } from "store/actions";
import { exportEmployeeToExcel } from "pages/exports/exportExcel";
import { exportEmployeeToPDF } from "pages/exports/exportPDF";
import toast from "react-hot-toast";

const Reports = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen((prev) => !prev);
  const {
    dropdownAutoList,
    claimReports,
    loading,
  } = useSelector((state) => ({
    dropdownAutoList: state.EmployeeDetailsReducer?.dropdownAutoList ?? [],
    claimReports: state.ReimbursementReportsReducer?.claimReports ?? [],
    loading: state.EmployeeDetailsReducer?.loading || state.ReimbursementReportsReducer?.loading,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0]);
    const unitId = authUserDetails[0].unit_id;
    dispatch(fetchEmployeeDropdownAutoList(unitId));
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      employee: "",
      fromDate: null,
      toDate: null,
    },
    validationSchema: Yup.object({
      fromDate: Yup.date().nullable().required("From Date is required"),
      toDate: Yup.date()
        .nullable()
        .required("To Date is required")
        .min(Yup.ref("fromDate"), "To Date must be after From Date"),
    }),
    onSubmit: (values) => {
      console.log("Search submitted:", values);
      const formData = new FormData();
      formData.append("emp_id", values.employee?.value || 0);
      formData.append("from_date", values.fromDate || null);
      formData.append("to_date", values.toDate || null);
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("create_by", authDetails?.user_id);
      dispatch(fetchClaimReports(formData));
    },
  });

  const columns = [
    { Header: "S.No", Cell: ({ row }) => claimReports.length - row.index, className: "serial-no text-center", applyHeaderClass: true },
    { Header: "Name", accessor: "emp_name" },
    { Header: "Date", accessor: "claim_date" },
    { Header: "Department", accessor: "department_name" },
    { Header: "Designation", accessor: "designation" },
    { Header: "Head", accessor: "head_name" },
    { Header: "Subhead", accessor: "subheads_name" },
    { Header: "Amount", accessor: "amount" },
    { Header: "Status", accessor: "claim_status" },
    {
      Header: "Action",
      className: "action-col",
      applyHeaderClass: true,
      Cell: ({ row }) => {
        const fileUrl = row.original.file_path;
        const filePath = fileUrl?.startsWith('http') ? fileUrl : `${process.env.REACT_APP_IMAGE_BASE_URL}${fileUrl}`;
        return (
          <a href={filePath} target="_blank" rel="noopener noreferrer">
            <i className="mdi mdi-eye text-secondary font-size-18" id={`view-tooltip-${row.id}`} />
            <UncontrolledTooltip placement="top" target={`view-tooltip-${row.id}`}>
              View Details
            </UncontrolledTooltip>
          </a>
        );
      },
      style: { width: "80px" },
    },
  ];

  const handleReset = () => {
    formik.resetForm();
  };

  const handleExportExcel = () => {
    if (!Array.isArray(claimReports) || claimReports.length === 0) {
      toast.error("No data to export.");
      return;
    }
    exportEmployeeToExcel(claimReports, authDetails?.unit_fullname || "Company", { address: authDetails?.unit_address || "" }, "Reimbursement_Report");
  };

  const handleExportPDF = () => {
    if (!Array.isArray(claimReports) || claimReports.length === 0) {
      toast.error("No data to export.");
      return;
    }
    exportEmployeeToPDF(claimReports, authDetails?.unit_fullname || "Company", { address: authDetails?.unit_address || "" }, "Reimbursement_Report", columns);
  };

  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Reimbursement" breadcrumbItem="Reports" />

        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit}>
              <Row className="mb-3">
                <Col md={3}>
                  <FormGroup>
                    <Label for="employee">Employee</Label>
                    <CustomSelect
                      id="employee"
                      name="employee"
                      options={(dropdownAutoList || []).map(emp => ({ value: emp.emp_id.toString(), label: emp.emp_no }))}
                      value={formik.values.employee || null}
                      onChange={(selectedOption) => formik.setFieldValue("employee", selectedOption)}
                      onBlur={formik.handleBlur}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>From <span className="text-danger">*</span></Label>
                    <CustomDatePicker
                      name="fromDate"
                      value={formik.values.fromDate}
                      onChange={(date) => formik.setFieldValue("fromDate", date)}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.fromDate && formik.errors.fromDate && (
                      <div className="text-danger">{formik.errors.fromDate}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>To <span className="text-danger">*</span></Label>
                    <CustomDatePicker
                      name="toDate"
                      value={formik.values.toDate}
                      onChange={(date) => formik.setFieldValue("toDate", date)}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.toDate && formik.errors.toDate && (
                      <div className="text-danger">{formik.errors.toDate}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col className="d-flex align-items-center justify-content-end">
                  <Button type="submit" className="btn button-search me-2">
                    <i className="fas fa-search me-1" /> Search
                  </Button>
                  <Button type="button" onClick={handleReset} className="btn button-cancel me-2">
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                  <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} direction="down">
                    <DropdownToggle color="primary" className="btn button-export">
                      <i className="fas fa-file-export me-1" /> Export
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem onClick={handleExportExcel}>
                        <i className="fas fa-file-excel me-2 text-success" /> Export to Excel
                      </DropdownItem>
                      <DropdownItem onClick={handleExportPDF}>
                        <i className="fas fa-file-pdf me-2 text-danger" /> Export to PDF
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={claimReports}
              isGlobalFilter={true}
              customPageSize={10}
              tableSorter={true}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default Reports;
